'use client'

import { IIconProps, Icon } from '@/common/components/Icon'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { Loader } from 'lucide-react'
import { forwardRef, useMemo, useState } from 'react'

export interface SelectOption {
  value: string
  label: string | React.ReactNode
  textValue?: string
  disabled?: boolean
}

export interface SelectOptionGroup {
  label: string
  options: SelectOption[]
}

interface SelectFieldProps {
  label?: string | React.ReactNode
  required?: boolean
  outline?: boolean
  iconBefore?: IIconProps
  iconAfter?: IIconProps
  error?: string
  loading?: boolean
  wrapperClassName?: string
  labelClassName?: string
  triggerClassName?: string
  contentClassName?: string
  placeholder?: string
  options?: SelectOption[]
  optionGroups?: SelectOptionGroup[]
  value?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
  name?: string
  searchable?: boolean
  searchPlaceholder?: string
  /**
   * Enables backend-driven search. When provided, the field stops filtering
   * options client-side and calls this (debounced) with the query instead.
   */
  onSearch?: (query: string) => void
  searchLoading?: boolean
}

const getOptionText = (option: SelectOption): string => {
  if (option.textValue) return option.textValue
  if (typeof option.label === 'string') return option.label
  return ''
}

const SelectField = forwardRef<HTMLButtonElement, SelectFieldProps>(
  (
    {
      label,
      required,
      outline,
      iconBefore,
      iconAfter,
      error,
      loading,
      wrapperClassName,
      labelClassName,
      triggerClassName,
      contentClassName,
      placeholder,
      options,
      optionGroups,
      value,
      onValueChange,
      disabled,
      name,
      searchable,
      searchPlaceholder = 'Search...',
      onSearch,
      searchLoading,
    }: SelectFieldProps,
    ref,
  ) => {
    const triggerClass = cn(
      '!bg-transparent border-x-0 border-t-0 backdrop-blur-xl border-b-monochrome-2/40 rounded-none focus:ring-0 focus:ring-offset-0 data-[placeholder]:font-normal data-[placeholder]:text-monochrome-2 text-monochrome-1 text-[11px] leading-[14px] font-medium h-[36px] px-0 pb-3 pt-0',
      outline &&
        '!bg-monochrome-3-60 h-[44px] rounded-[16px] !border border-monochrome-120 py-[10px] pl-[15px] pr-[12px] text-[14px] font-bold leading-[20px]',
      iconBefore && 'ps-10',
      iconAfter && 'pe-10',
      error && 'border-accent-3 focus:border-accent-3',
      triggerClassName,
    )

    return (
      <div className={cn('grid w-full gap-1', wrapperClassName)}>
        {label && (
          <Label
            className={cn('text-body-medium text-monochrome-1', labelClassName)}
          >
            {label}
            {required && <span className="text-red-500">*</span>}
          </Label>
        )}
        <div className="relative">
          {iconBefore && (
            <Icon
              className={cn(
                'absolute left-4 z-10 pointer-events-none',
                outline ? 'top-1/2 -translate-y-1/2' : 'h-[36px]',
                iconBefore.className,
              )}
              icon={iconBefore.icon}
              width={iconBefore.width}
              height={iconBefore.height}
              viewBox={iconBefore.viewBox}
            />
          )}
          {searchable ? (
            <SearchableSelect
              ref={ref}
              triggerClass={triggerClass}
              contentClassName={contentClassName}
              placeholder={placeholder}
              searchPlaceholder={searchPlaceholder}
              options={options}
              optionGroups={optionGroups}
              value={value}
              onValueChange={onValueChange}
              disabled={disabled || loading}
              onSearch={onSearch}
              searchLoading={searchLoading}
            />
          ) : (
            <Select
              value={value}
              onValueChange={onValueChange}
              disabled={disabled || loading}
              name={name}
            >
              <SelectTrigger ref={ref} className={triggerClass}>
                <SelectValue
                  placeholder={placeholder}
                  className="[data-placeholder]:font-semibold [data-placeholder]:text-base placeholder:text-monochrome-2"
                />
              </SelectTrigger>
              <SelectContent
                className={cn(
                  'bg-monochrome-3-60 backdrop-blur-[10px] border-2 border-white/10 max-h-[400px] overflow-y-auto',
                  contentClassName,
                )}
              >
                {optionGroups ? (
                  optionGroups.map((group, groupIndex) => (
                    <SelectGroup key={`group-${groupIndex}`} className="w-full">
                      <SelectLabel>{group.label}</SelectLabel>
                      {group.options.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                          textValue={option.textValue}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))
                ) : (
                  <SelectGroup className="w-full">
                    {options?.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        textValue={option.textValue}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          )}
          {loading && (
            <div
              className={cn(
                'absolute right-4 pointer-events-none',
                outline ? 'top-1/2 -translate-y-1/2' : 'top-[6px]',
              )}
            >
              <Loader className="animate-spin origin-center text-monochrome1/60" />
            </div>
          )}
          {iconAfter && !loading && (
            <Icon
              className={cn(
                'absolute right-4 pointer-events-none',
                outline ? 'top-1/2 -translate-y-1/2' : 'top-[6px]',
                iconAfter.className,
              )}
              icon={iconAfter.icon}
              width={iconAfter.width}
              height={iconAfter.height}
              viewBox={iconAfter.viewBox}
            />
          )}
        </div>
        {error && <span className="text-xs text-accent-3 mt-1">{error}</span>}
      </div>
    )
  },
)

SelectField.displayName = 'SelectField'

interface SearchableSelectProps {
  triggerClass: string
  contentClassName?: string
  placeholder?: string
  searchPlaceholder: string
  options?: SelectOption[]
  optionGroups?: SelectOptionGroup[]
  value?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
  onSearch?: (query: string) => void
  searchLoading?: boolean
}

const SearchableSelect = forwardRef<HTMLButtonElement, SearchableSelectProps>(
  (
    {
      triggerClass,
      contentClassName,
      placeholder,
      searchPlaceholder,
      options,
      optionGroups,
      value,
      onValueChange,
      disabled,
      onSearch,
      searchLoading,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')

    const handleSearchChange = (query: string) => {
      setSearch(query)
    }

    const runSearch = (query: string) => {
      if (!onSearch) return
      onSearch(query)
    }

    const flatOptions = useMemo(
      () => optionGroups?.flatMap((group) => group.options) ?? options ?? [],
      [options, optionGroups],
    )

    const selectedLabel = useMemo(
      () => flatOptions.find((option) => option.value === value)?.label,
      [flatOptions, value],
    )

    // Backend-driven search: options arrive pre-filtered, so skip local filtering.
    const isAsync = !!onSearch

    const matches = (option: SelectOption) =>
      getOptionText(option).toLowerCase().includes(search.trim().toLowerCase())

    const filteredGroups = useMemo(
      () =>
        isAsync
          ? optionGroups
          : optionGroups
              ?.map((group) => ({
                ...group,
                options: group.options.filter(matches),
              }))
              .filter((group) => group.options.length > 0),
      [optionGroups, search, isAsync],
    )

    const filteredOptions = useMemo(
      () => (isAsync ? options : options?.filter(matches)),
      [options, search, isAsync],
    )

    const handleSelect = (option: SelectOption) => {
      if (option.disabled) return
      onValueChange?.(option.value)
      setOpen(false)
      setSearch('')
    }

    const renderItem = (option: SelectOption) => (
      <CommandItem
        key={option.value}
        value={option.value}
        disabled={option.disabled}
        onSelect={() => handleSelect(option)}
        className={cn(
          'px-4 py-3 border-b border-white/10 last:border-b-0 aria-selected:bg-accent',
          option.value === value && 'font-bold text-accent-1',
        )}
      >
        {option.label}
      </CommandItem>
    )

    return (
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next && search) {
            setSearch('')
            onSearch?.('')
          }
        }}
        modal
      >
        <PopoverTrigger asChild disabled={disabled}>
          <button
            ref={ref}
            type="button"
            className={cn(
              triggerClass,
              'flex w-full items-center justify-between gap-[10px] text-left disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <span
              className={cn(
                'truncate',
                selectedLabel == null && 'font-normal text-monochrome-2',
              )}
            >
              {selectedLabel ?? placeholder}
            </span>
            <span className="flex shrink-0 items-center justify-center px-[8px] py-[4px]">
              <Icon
                icon="arrow-up-down"
                width="8"
                height="16"
                viewBox="0 0 8 16"
              />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn(
            'w-[var(--radix-popover-trigger-width)] p-2 bg-monochrome-3-60 backdrop-blur-[10px] border-2 border-white/10 max-h-[400px] overflow-hidden',
            contentClassName,
          )}
        >
          <Command shouldFilter={false} className="bg-transparent">
            <CommandInput
              value={search}
              onValueChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                if (!onSearch) return
                e.preventDefault()
                e.stopPropagation()
                runSearch(search)
              }}
              placeholder={searchPlaceholder}
            />
            <CommandList className="mt-2 max-h-[320px] -mx-2">
              {searchLoading ? (
                <div className="flex items-center justify-center py-6 text-monochrome-2">
                  <Loader className="h-4 w-4 animate-spin" />
                </div>
              ) : (
                <>
                  <CommandEmpty>No results found.</CommandEmpty>
                  {filteredGroups ? (
                    filteredGroups.map((group, groupIndex) => (
                      <CommandGroup
                        key={`group-${groupIndex}`}
                        heading={group.label}
                      >
                        {group.options.map(renderItem)}
                      </CommandGroup>
                    ))
                  ) : (
                    <CommandGroup>
                      {filteredOptions?.map(renderItem)}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)

SearchableSelect.displayName = 'SearchableSelect'

export default SelectField
